import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import type {
    MarketplaceListing,
    InsertMarketplaceListing,
    UpdateMarketplaceListing,
    MarketplaceListingQueryParams,
    MarketplaceCategory,
    InsertMarketplaceCategory,
    UpdateMarketplaceCategory,
    MarketplaceBrand,
    InsertMarketplaceBrand,
    UpdateMarketplaceBrand,
    MarketplaceListingImage,
    ReportListingForm,
    MarketplaceDashboard,
} from '@baboor/baboor-models'
import { ApiResponse } from '@baboor/common-models'
import { toHttpParams } from '@baboor/common-util'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class MarketplaceApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/v1/marketplace`
    }

    getListings(
        params?: MarketplaceListingQueryParams,
    ): Observable<ApiResponse<MarketplaceListing[]>> {
        return this.http.get<ApiResponse<MarketplaceListing[]>>(
            `${this.apiUrl}/listings`,
            { params: toHttpParams(params ?? {}) },
        )
    }

    getListing(id: string): Observable<ApiResponse<MarketplaceListing>> {
        return this.http.get<ApiResponse<MarketplaceListing>>(
            `${this.apiUrl}/listings/${id}`,
        )
    }

    createListing(
        data: InsertMarketplaceListing,
    ): Observable<ApiResponse<MarketplaceListing>> {
        return this.http.post<ApiResponse<MarketplaceListing>>(
            `${this.apiUrl}/listings`,
            data,
        )
    }

    updateListing(
        id: string,
        data: UpdateMarketplaceListing,
    ): Observable<ApiResponse<MarketplaceListing>> {
        return this.http.patch<ApiResponse<MarketplaceListing>>(
            `${this.apiUrl}/listings/${id}`,
            data,
        )
    }

    deleteListing(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/listings/${id}`,
        )
    }

    getCategories(): Observable<ApiResponse<MarketplaceCategory[]>> {
        return this.http.get<ApiResponse<MarketplaceCategory[]>>(
            `${this.apiUrl}/categories`,
        )
    }

    createCategory(
        data: InsertMarketplaceCategory,
    ): Observable<ApiResponse<MarketplaceCategory>> {
        return this.http.post<ApiResponse<MarketplaceCategory>>(
            `${this.apiUrl}/categories`,
            data,
        )
    }

    updateCategory(
        id: string,
        data: UpdateMarketplaceCategory,
    ): Observable<ApiResponse<MarketplaceCategory>> {
        return this.http.patch<ApiResponse<MarketplaceCategory>>(
            `${this.apiUrl}/categories/${id}`,
            data,
        )
    }

    deleteCategory(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/categories/${id}`,
        )
    }

    getBrands(): Observable<ApiResponse<MarketplaceBrand[]>> {
        return this.http.get<ApiResponse<MarketplaceBrand[]>>(
            `${this.apiUrl}/brands`,
        )
    }

    createBrand(
        data: InsertMarketplaceBrand,
    ): Observable<ApiResponse<MarketplaceBrand>> {
        return this.http.post<ApiResponse<MarketplaceBrand>>(
            `${this.apiUrl}/brands`,
            data,
        )
    }

    updateBrand(
        id: string,
        data: UpdateMarketplaceBrand,
    ): Observable<ApiResponse<MarketplaceBrand>> {
        return this.http.patch<ApiResponse<MarketplaceBrand>>(
            `${this.apiUrl}/brands/${id}`,
            data,
        )
    }

    deleteBrand(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/brands/${id}`,
        )
    }

    uploadListingImage(
        listingId: string,
        file: File,
    ): Observable<ApiResponse<MarketplaceListingImage>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<MarketplaceListingImage>>(
            `${this.apiUrl}/listings/${listingId}/images`,
            formData,
        )
    }

    deleteListingImage(imageId: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/images/${imageId}`,
        )
    }

    setCoverImage(imageId: string): Observable<ApiResponse<unknown>> {
        return this.http.patch<ApiResponse<unknown>>(
            `${this.apiUrl}/images/${imageId}/cover`,
            {},
        )
    }

    toggleFavorite(
        listingId: string,
    ): Observable<ApiResponse<{ isFavorited: boolean }>> {
        return this.http.post<ApiResponse<{ isFavorited: boolean }>>(
            `${this.apiUrl}/listings/${listingId}/favorite`,
            {},
        )
    }

    getFavorites(): Observable<ApiResponse<MarketplaceListing[]>> {
        return this.http.get<ApiResponse<MarketplaceListing[]>>(
            `${this.apiUrl}/favorites`,
        )
    }

    reportListing(data: ReportListingForm): Observable<ApiResponse<unknown>> {
        return this.http.post<ApiResponse<unknown>>(
            `${this.apiUrl}/reports`,
            data,
        )
    }

    getMyListings(
        params?: MarketplaceListingQueryParams,
    ): Observable<ApiResponse<MarketplaceListing[]>> {
        return this.http.get<ApiResponse<MarketplaceListing[]>>(
            `${this.apiUrl}/my-listings`,
            { params: toHttpParams(params ?? {}) },
        )
    }

    publishListing(id: string): Observable<ApiResponse<MarketplaceListing>> {
        return this.http.patch<ApiResponse<MarketplaceListing>>(
            `${this.apiUrl}/listings/${id}/publish`,
            {},
        )
    }

    getDashboard(): Observable<ApiResponse<MarketplaceDashboard>> {
        return this.http.get<ApiResponse<MarketplaceDashboard>>(
            `${this.apiUrl}/dashboard`,
        )
    }

    adminGetListings(
        params?: MarketplaceListingQueryParams,
    ): Observable<ApiResponse<MarketplaceListing[]>> {
        return this.http.get<ApiResponse<MarketplaceListing[]>>(
            `${this.apiUrl}/admin/listings`,
            { params: toHttpParams(params ?? {}) },
        )
    }

    adminUpdateListingStatus(
        id: string,
        status: string,
    ): Observable<ApiResponse<MarketplaceListing>> {
        return this.http.patch<ApiResponse<MarketplaceListing>>(
            `${this.apiUrl}/admin/listings/${id}/status`,
            { status },
        )
    }
}
